import { useAvailableRegisterKeys } from "../../../../hooks/useFacilityLocationQueries";
import FilterableConfigList from "./FilterableConfigList";

type RegisterKeyOption = { value: string; label: string };

export function useRegisterKeyOptions(): {
    registerKeyOptions: RegisterKeyOption[];
    registerLabelByKey: Record<string, string>;
    } {
    const { data: availableRegisterKeys } = useAvailableRegisterKeys();
    const registerKeyOptions: RegisterKeyOption[] = (availableRegisterKeys || []).map((item: any) => ({
        value: item.key,
        label: item.label || item.key,
    }));
    const registerLabelByKey: Record<string, string> = (availableRegisterKeys || []).reduce((acc: Record<string, string>, item: any) => {
        acc[item.key] = item.label || item.key;
        return acc;
    }, {});

    return { registerKeyOptions, registerLabelByKey };
}

export default function RegisterKeyConfigList({
    name,
    title,
    description,
    fields,
    setArray,
    labelPlaceholder,
    colorPlaceholder,
}: {
    name: string;
    title: string;
    description: string | JSX.Element;
    fields: any[];
    setArray: (next: any[]) => void;
    labelPlaceholder?: string;
    colorPlaceholder?: string;
}) {
    const { registerKeyOptions, registerLabelByKey } = useRegisterKeyOptions();

    const getFieldForm = (object: any, setObject: any) => ([
        {
            label: "Register",
            name: "key",
            type: "single-select",
            options: registerKeyOptions,
            multi: false,
            onchange_callback: ({ value }) => {
                const nextLabel = registerLabelByKey?.[value] || value;
                setObject({ key: value, label: nextLabel });
            }
        },
        {
            label: "Label (optional)",
            name: "label",
            type: "text",
            placeholder: labelPlaceholder || "Co2",
            description: "Human-readable label (defaults to selected register label).",
        },
        {
            label: "SMA window n (optional)",
            name: "sma_n",
            type: "number",
            placeholder: "6",
            description: "Simple moving average window size. If set > 1, the line will be smoothed using the last n points.",
        },
        {
            label: "Color (optional)",
            name: "color",
            type: "text",
            placeholder: colorPlaceholder || "#1f77b4",
            description: "Hex color for this line/axis (e.g. #ff0000). Leave empty for default colors.",
        },
        {
            label: "Min (optional)",
            name: "min",
            type: "number",
            placeholder: "0",
            description: "Optional fixed lower bound for this register axis. Overrides the register's own min for this graph only.",
        },
        {
            label: "Max (optional)",
            name: "max",
            type: "number",
            placeholder: "100",
            description: "Optional fixed upper bound for this register axis. Overrides the register's own max for this graph only.",
        },
    ]);

    return (
        <FilterableConfigList
            name={name}
            title={title}
            description={description}
            useDisplayFilter={["fruit_types", "exclude_fruit_types"]}
            fields={fields || []}
            setArray={setArray as any}
            defaultField={{ key: "", label: "", color: "" }}
            getFieldTitle={(field: any) => (
                <span>
                    <b>{field?.label || field?.key || "Select register"}</b>
                    {field?.color ? <span className="text-muted"> ({field.color})</span> : null}
                </span>
            )}
            getFieldForm={getFieldForm}
        />
    );
}

